import React, { useState, useEffect, useRef } from 'react';

interface User {
  id: string;
  email: string;
  name: string;
  isAdmin: boolean;
}

interface UserSelectorProps {
  getToken: () => Promise<string>;
  apiUrl: string;
  onSelect: (userId: string) => void;
  onCancel: () => void;
  currentUserId?: string;
}

const UserSelector: React.FC<UserSelectorProps> = ({
  getToken,
  apiUrl,
  onSelect,
  onCancel,
  currentUserId
}) => {
  const [users, setUsers] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const token = await getToken();
        const response = await fetch(`${apiUrl}/api/users`, {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          // Exclude the current user (the one currently holding the cart)
          const filteredData = currentUserId
            ? data.filter((u: User) => u.id !== currentUserId)
            : data;
          setUsers(filteredData);
        } else {
          console.error('Failed to fetch users');
        }
      } catch (error) {
        console.error('Error fetching users:', error);
      } finally {
        setLoading(false);
      }
    };

    if (apiUrl) {
      fetchUsers();
    }
  }, [apiUrl, getToken, currentUserId]);

  useEffect(() => {
    // Auto-focus search for better UX
    searchInputRef.current?.focus();
  }, []);

  // Filter logic performed during render for immediate feedback
  const filteredUsers = users.filter(
    (user) =>
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[100]">
      <div className="bg-white rounded-3xl p-6 max-w-md w-full shadow-2xl animate-in fade-in zoom-in duration-200">
        <div className="mb-6">
          <h3 className="text-xl font-bold text-gray-800">Transfer Cart</h3>
          <p className="text-sm text-gray-500">Search for the user receiving this cart.</p>
        </div>

        {/* Search Input */}
        <div className="relative mb-4">
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white outline-none transition-all"
          />
        </div>

        {/* User List Container */}
        <div className="max-h-64 overflow-y-auto border border-gray-100 rounded-xl divide-y scrollbar-hide">
          {loading ? (
            <div className="p-10 text-center text-gray-400">
              <div className="animate-spin h-6 w-6 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-2"></div>
              Loading users...
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="p-10 text-center text-gray-400">
              {searchTerm ? 'No matches found' : 'No users available'}
            </div>
          ) : (
            filteredUsers.map((user) => (
              <button
                key={user.id}
                onClick={() => setSelectedUser(user)}
                className={`w-full text-left px-4 py-3 flex items-center justify-between transition-colors ${
                  selectedUser?.id === user.id 
                    ? 'bg-blue-50 border-l-4 border-blue-500' 
                    : 'hover:bg-gray-50'
                }`}
              >
                <div>
                  <div className="font-semibold text-gray-900">{user.name}</div>
                  <div className="text-xs text-gray-500">{user.email}</div>
                </div>
                {user.isAdmin && (
                  <span className="text-[10px] bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-bold uppercase">
                    Admin
                  </span>
                )}
              </button>
            ))
          )}
        </div>

        {/* Selected User Badge */}
        {selectedUser && (
          <div className="mt-4 p-3 bg-blue-50 rounded-xl flex items-center gap-2 animate-in slide-in-from-top-2">
            <div className="bg-blue-500 text-white rounded-full p-1">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </div>
            <span className="text-sm font-medium text-blue-700">
              Transferring to <strong>{selectedUser.name}</strong>
            </span>
          </div>
        )}

        {/* Footer Actions */}
        <div className="flex gap-3 mt-8">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl font-bold hover:bg-gray-200 transition active:scale-95"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!selectedUser}
            onClick={() => onSelect(selectedUser!.id)}
            className={`flex-1 px-4 py-3 rounded-xl font-bold transition active:scale-95 ${
              selectedUser 
                ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg shadow-blue-200' 
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            Transfer Now
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserSelector;